import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    TextField, Button, Box, Typography, Paper, Grid, Card, CardMedia, CardContent, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { client } from '../api/Api';

const ProductEdit = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [product, setProduct] = useState({
        name: '',
        description: '',
        category: '',
        alias: '',
        prices: [],
        imageUrl: '', // This should match your backend property
    });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [imagePreview, setImagePreview] = useState('');
    const [newImageFile, setNewImageFile] = useState(null);

    useEffect(() => {
        if (productId) {
            client.get(`/products/product/${productId}`)
                .then(response => {
                    setProduct(response.data);
                    setImagePreview(response.data.image || '');
                    setLoading(false);
                })
                .catch(error => {
                    setSnackbar({ open: true, message: "Error loading product", severity: "error" });
                    setLoading(false);
                });
        }
    }, [productId]);

    const DEFAULT_IMAGE_PATH = "/img/img.png";

    const getImageUrl = (imagePath) => {
        if (!imagePath) return process.env.PUBLIC_URL + DEFAULT_IMAGE_PATH;

        // Default to uploads directory
        return `https://tikrai.com/api/images/${imagePath}.png`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProduct(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Image preview logic
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setNewImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const uploadImage = async (file) => {
        const formData = new FormData();
        formData.append("image", file);
        const response = await client.post('/products/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data.imageUrl;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);
        try {
            let updatedProduct = { ...product };

            // If a new image was selected, upload it first
            if (newImageFile) {
                const imageUrl = await uploadImage(newImageFile);
                updatedProduct.imageUrl = imageUrl;
            }

            await client.put(`/products/${product.id}`, updatedProduct);
            setSnackbar({ open: true, message: 'Product updated successfully!', severity: 'success' });

            setTimeout(() => navigate('/admin/products'), 1000);
        } catch (error) {
            setSnackbar({ open: true, message: 'Error updating product', severity: 'error' });
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300}}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ pt: 4, pb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#f8f8f8', minHeight: '100vh' }}>
            <Paper sx={{ p: 4, maxWidth: 700, width: '100%' }} elevation={6}>
                <Typography variant="h4" gutterBottom>Edit Product</Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                        <Card elevation={3} sx={{ mb: 2, textAlign: 'center' }}>
                            {imagePreview ? (
                                <CardMedia
                                    component="img"
                                    image={getImageUrl(product.image)}
                                    alt={product.name || "Product"}
                                    height="240"
                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = process.env.PUBLIC_URL + '/img/img.png'; }}
                                    sx={{ objectFit: 'contain', bgcolor: '#fafafd' }}
                                />
                            ) : (
                                <Box sx={{
                                    height: 240,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: '#ededed',
                                    color: '#888'
                                }}>
                                    <Typography variant="body2">No image</Typography>
                                </Box>
                            )}
                            <CardContent>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => fileInputRef.current.click()}
                                    size="small"
                                >
                                    {imagePreview ? "Change Image" : "Upload Image"}
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleImageChange}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <form onSubmit={handleSubmit} autoComplete="off">
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={product.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Description"
                                        name="description"
                                        value={product.description}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Category"
                                        name="category"
                                        value={product.category}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Alias"
                                        name="alias"
                                        value={product.alias}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={uploading}
                                        size="large"
                                        fullWidth
                                    >
                                        {uploading ? <CircularProgress size={24} /> : "Save Changes"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Grid>
                </Grid>
            </Paper>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={2500}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default ProductEdit;